import '../../styles/stock/offer.css';
import bindToForm from '../form/form-protection';
import '../../image/geld-lenen-kost-geld.svg';

class OfferWizard {
    /** @type {boolean} */
    #isWizardEnabled;

    /** @type {HTMLElement[]} */
    #stepElements = [];

    /** @type {string[]} */
    #stepLabels = [];

    /** @type {{next: string, previous: string, submitting: string, submit: string}} */
    #translations;

    /** @type {HTMLElement[]} */
    #dots;

    /** @type {HTMLElement[]} */
    #steps;

    /** @type {number} */
    #currentStepIndex;

    constructor(formElement) {
        this.form = formElement;
        this.#isWizardEnabled = formElement.getAttribute('data-wizard') === 'true';

        if (!this.#isWizardEnabled) {
            this.bindSubmitHandler();
            return;
        }

        this.extractTranslations();
        this.detectSteps();

        if (this.#stepElements.length === 0) {
            console.warn('OfferWizard: No form fields found in .wizard-container');
            return;
        }

        this.buildWizardUI();
        this.init();
    }

    extractTranslations() {
        // Read from form data-attributes
        this.#translations = {
            next: this.form.getAttribute('data-wizard-next') || 'Volgende',
            previous: this.form.getAttribute('data-wizard-previous') || 'Vorige',
            submitting: this.form.getAttribute('data-wizard-submitting') || 'Bezig met verzenden...',
        };

        const submitBtn = this.form.querySelector('button[type="submit"], input[type=submit]');
        this.#translations.submit = submitBtn?.textContent.trim() || 'Verzenden';
    }

    detectSteps() {
        const container = this.form.querySelector('.wizard-container');

        if (!container) {
            this.#stepElements = [];
            console.error('Offer | There is no wizard container so we cannot detect the steps.');

            return;
        }

        this.#stepElements = Array.from(container.querySelectorAll(':scope > .form-group'));
        this.#stepLabels = this.#stepElements.map((step) => {
            const label = step.querySelector('label');
            return label?.textContent.trim() || '';
        });
    }

    buildWizardUI() {
        this.createProgressIndicator();
        this.wrapStepsInContainers();
        this.addNavigationButtons();
        this.hideOriginalSubmitButton();
    }

    createProgressIndicator() {
        const progress = document.createElement('div');
        progress.classList.add('wizard-progress', 'flex', 'items-center', 'justify-center', 'gap-1.5', 'mt-6');

        this.#stepElements.forEach((_, index) => {
            const dot = document.createElement('div');
            if (index === 0) {
                dot.classList.add('h-1', 'md:h-1.5', 'rounded-full', 'transition-all', 'bg-theme-primary', 'w-6', 'md:w-8');
            } else {
                dot.classList.add('h-1', 'md:h-1.5', 'rounded-full', 'transition-all', 'bg-theme-primary/20', 'w-3', 'md:w-4');
            }
            progress.appendChild(dot);
        });

        const container = this.form.querySelector('.wizard-container');
        container.parentNode.insertBefore(progress, container);

        this.#dots = Array.from(progress.children);
    }

    wrapStepsInContainers() {
        this.#stepElements.forEach((formGroup, index) => {
            const wrapper = document.createElement('div');
            wrapper.classList.add('wizard-step');
            wrapper.dataset.stepIndex = index;
            wrapper.dataset.stepLabel = this.#stepLabels[index];

            if (index !== 0) {
                wrapper.classList.add('hidden');
            }

            formGroup.parentNode.insertBefore(wrapper, formGroup);
            wrapper.appendChild(formGroup);
        });

        this.#steps = Array.from(this.form.querySelectorAll('.wizard-step'));
    }

    addNavigationButtons() {
        this.#steps.forEach((step, index) => {
            const nav = this.createNavigationButtons(index);
            step.appendChild(nav);
        });
    }

    createNavigationButtons(stepIndex) {
        const nav = document.createElement('div');
        nav.classList.add('wizard-nav', 'flex', 'gap-4', 'mt-6');

        const isFirst = stepIndex === 0;
        const isLast = stepIndex === this.#steps.length - 1;

        if (!isFirst) {
            const prevBtn = document.createElement('button');
            prevBtn.type = 'button';
            prevBtn.classList.add(
                'wizard-prev',
                'block',
                'bg-theme-primary/20',
                'text-text',
                'font-medium',
                'rounded-lg',
                'text-sm',
                'py-3',
                'px-5',
                'text-center',
                'hover:bg-theme-primary/30',
            );
            prevBtn.textContent = this.#translations.previous;
            nav.appendChild(prevBtn);
        }

        if (isLast) {
            const submitBtn = this.form.querySelector('[type=submit]');
            submitBtn.classList.add('wizard-submit', 'w-full');
            nav.appendChild(submitBtn);
        } else {
            const nextBtn = document.createElement('button');
            nextBtn.type = 'button';
            nextBtn.classList.add(
                'wizard-next',
                'block',
                'w-full',
                'bg-theme-primary',
                'text-on-primary',
                'font-medium',
                'rounded-lg',
                'text-sm',
                'py-3',
                'px-5',
                'text-center',
                'hover:bg-theme-primary/90',
            );
            nextBtn.textContent = this.#translations.next;
            nav.appendChild(nextBtn);
        }

        return nav;
    }

    hideOriginalSubmitButton() {
        // Find submit button outside wizard-container
        const allFormGroups = Array.from(this.form.querySelectorAll('.form-group'));
        const containerFormGroups = this.#stepElements;

        allFormGroups.forEach((formGroup) => {
            if (!containerFormGroups.includes(formGroup)) {
                const submitBtn = formGroup.querySelector('button[type="submit"]');
                if (submitBtn) {
                    formGroup.classList.add('hidden');
                }
            }
        });
    }

    init() {
        this.#currentStepIndex = 0;
        this.bindClearInvalidStates();
        this.bindWizardEvents();
        this.bindSubmitHandler();

        setTimeout(() => this.focusStepInput(this.#steps[0]), 50);
    }

    bindClearInvalidStates() {
        const inputs = this.form.querySelectorAll('input, textarea, select');
        for (const input of inputs) {
            input.addEventListener('input', () => {
                input.classList.remove('invalid');
            });

            input.addEventListener('change', () => {
                input.classList.remove('invalid');
            });
        }
    }

    bindSubmitHandler() {
        this.form.addEventListener('submit', () => {
            const submitButtons = this.form.querySelectorAll('button[type="submit"], input[type=submit]');
            for (const button of submitButtons) {
                button.disabled = true;
                button.innerHTML = `<span class="inline-block animate-spin mr-2">↻</span>${this.#translations.submitting}`;
            }
            return true;
        });
    }

    bindWizardEvents() {
        this.form.addEventListener('click', (e) => {
            if (e.target.matches('.wizard-next')) {
                e.preventDefault();
                this.nextStep();
            } else if (e.target.matches('.wizard-prev')) {
                e.preventDefault();
                this.prevStep();
            }
        });

        this.#steps.forEach((step, index) => {
            const inputs = step.querySelectorAll('input, textarea, select');
            for (const input of inputs) {
                input.addEventListener('keydown', (e) => {
                    if (e.key !== 'Enter') {
                        return;
                    }

                    e.preventDefault();
                    e.stopPropagation();

                    const isLastStep = index === this.#steps.length - 1;

                    if (isLastStep) {
                        this.form.requestSubmit();
                    } else {
                        this.nextStep();
                    }
                });
            }
        });
    }

    validateCurrentStep() {
        const currentStep = this.#steps[this.#currentStepIndex];
        const inputs = currentStep.querySelectorAll('input, textarea, select');

        for (const input of inputs) {
            if (!input.checkValidity()) {
                input.classList.add('invalid');
                input.focus();
                return false;
            }
        }

        return true;
    }

    nextStep() {
        if (!this.validateCurrentStep()) {
            return;
        }

        if (this.#currentStepIndex < this.#steps.length - 1) {
            this.updateStep(this.#currentStepIndex + 1);
        }
    }

    prevStep() {
        if (this.#currentStepIndex > 0) {
            this.updateStep(this.#currentStepIndex - 1);
        }
    }

    updateStep(newStepIndex) {
        const oldStep = this.#steps[this.#currentStepIndex];
        const newStep = this.#steps[newStepIndex];

        oldStep.classList.add('hidden');
        oldStep.classList.remove('active');

        newStep.classList.remove('hidden');
        newStep.classList.add('active');

        this.updateProgressDots(newStepIndex);

        this.#currentStepIndex = newStepIndex;

        setTimeout(() => this.focusStepInput(newStep), 50);
    }

    updateProgressDots(activeStepIndex) {
        this.#dots.forEach((dot, index) => {
            if (index === activeStepIndex) {
                dot.classList.remove('bg-theme-primary/20', 'w-3', 'md:w-4');
                dot.classList.add('bg-theme-primary', 'w-6', 'md:w-8');
            } else {
                dot.classList.remove('bg-theme-primary', 'w-6', 'md:w-8');
                dot.classList.add('bg-theme-primary/20', 'w-3', 'md:w-4');
            }
        });
    }

    focusStepInput(stepElement) {
        const focusableInput = stepElement.querySelector(
            'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
        );

        if (focusableInput) {
            focusableInput.focus();
        }
    }
}

// Initialize wizard on forms with data-wizard attribute
for (const formElement of document.querySelectorAll('form[data-wizard]')) {
    new OfferWizard(formElement);
}

// Initialize form protection
for (const formElement of document.querySelectorAll('form[data-protect]')) {
    bindToForm(formElement, 'Weet je zeker dat je de pagina wilt verlaten?');
}
